import React, { useCallback } from 'react';

import { Alert, Box, CircularProgress } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useAccommodationServiceFetch } from '~/components/Accommodation/hooks/useAccommodationServiceFetch';
import VerificationStatusChip from '~/components/Common/VerificationStatusChip';

import { MappingLog, getMappingLogs } from '~/services/AccommodationService';
import { formatDateOrdinalWithClock } from '~/services/TimeService';

const columns: Array<GridColDef<MappingLog>> = [
  {
    field: 'action',
    headerName: 'Verified?',
    minWidth: 100,
    display: 'flex',
    hideable: true,
    renderCell: (params) => <VerificationStatusChip status={params.value} />,
  },
  {
    field: 'actionedBy',
    headerName: 'Actioned By',
    minWidth: 350,
    display: 'flex',
    hideable: true,
  },
  {
    field: 'createdAt',
    headerName: 'Date',
    valueFormatter: (value) => formatDateOrdinalWithClock(new Date(value as string)),
    minWidth: 200,
    display: 'flex',
    hideable: true,
  },
  {
    field: 'actionNotes',
    headerName: 'Comments',
    minWidth: 300,
    display: 'flex',
    hideable: true,
  },
];

interface Props {
  type: 'room' | 'property';
  targetId: string;
  sourceId: string;
}

export default function MappingLogTable({ type, targetId, sourceId }: Props) {
  const fetchMappingLogs = useCallback(
    () =>
      getMappingLogs({
        sourceModel: type === 'room' ? 'internal_rooms' : 'internal_properties',
        sourceId: sourceId,
        targetModel: type === 'room' ? 'rooms' : 'properties',
        targetId: targetId,
      }),
    [sourceId, targetId, type],
  );

  const { fetchReqState } = useAccommodationServiceFetch({
    fetchFn: fetchMappingLogs,
  });

  if (fetchReqState.status === 'pending' || fetchReqState.status === 'uninitiated') {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress variant="indeterminate" />
      </Box>
    );
  }

  if (fetchReqState.status === 'failed') {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {JSON.stringify(fetchReqState.error)}
      </Alert>
    );
  }

  return (
    <DataGrid
      className="mapping-logs-table"
      columns={columns}
      rows={fetchReqState.status === 'succeeded' ? fetchReqState.result : []}
      getRowHeight={() => 'auto'}
      autoHeight
      disableRowSelectionOnClick
      hideFooterPagination
      disableColumnFilter
      disableColumnSelector
      disableDensitySelector
    />
  );
}
